import UNINITIALISED from "utils/uninitialised";
import {MaybeUninitialised} from "modules/redux_types";
import {GetWorkflowsAction} from "../actions/workflows_fetch";
import {WorkflowAddAction} from "../actions/workflow_add";
import {WorkflowUpdateAction} from "../actions/workflow_update";
import {WorkflowDuplicateAction} from "../actions/workflow_duplicate";
import {WorkflowRemoveAction} from "../actions/workflow_remove";
import actionTypes from "../constants/action_types";
import {
  WorkflowInfo,
  WorkflowId,
  WorkflowInstance,
} from "common/flowmaster/types/workflow";
import {WorkflowInstancesFetchAction} from "../actions/workflow_instances_fetch";
import {WorkflowInstanceAddAction} from "../actions/workflow_instance_add";
import {WorkflowInstanceUpdateNameAction} from "../actions/workflow_instance_update_name";
import {WorkflowInstanceUpdateIsArchivedAction} from "../actions/workflow_instance_update_is_archived";
import {WorkflowInstanceUpdateIsPublicAction} from "../actions/workflow_instance_update_is_public";
import {WorkflowInstanceUpdateDefaultAssetNameAction} from "../actions/workflow_instance_update_default_asset_name";

interface WorkflowWithInstances extends WorkflowInfo {
  workflow_instances?: WorkflowInstance[];
}
export default function workflowsReducer(
  state: MaybeUninitialised<WorkflowWithInstances[]> = UNINITIALISED,
  action:
    | GetWorkflowsAction
    | WorkflowAddAction
    | WorkflowUpdateAction
    | WorkflowDuplicateAction
    | WorkflowRemoveAction
    | WorkflowInstancesFetchAction
    | WorkflowInstanceAddAction
    | WorkflowInstanceUpdateNameAction
    | WorkflowInstanceUpdateIsArchivedAction
    | WorkflowInstanceUpdateIsPublicAction
    | WorkflowInstanceUpdateDefaultAssetNameAction,
): typeof state {
  if (action.type === actionTypes.WORKFLOWS_FETCH.SUCCESS) {
    return (action as GetWorkflowsAction).payload;
  }
  if (state !== UNINITIALISED) {
    if (action.type === actionTypes.WORKFLOW_ADD.SUCCESS) {
      const newWorkflow: WorkflowInfo = (action as WorkflowAddAction).payload;
      return [...state, newWorkflow];
    } else if (action.type === actionTypes.WORKFLOW_DUPLICATE.SUCCESS) {
      const duplicateWorkflow: WorkflowInfo = (action as WorkflowDuplicateAction)
        .payload;
      return [...state, duplicateWorkflow];
    } else if (action.type === actionTypes.WORKFLOW_UPDATE.SUCCESS) {
      const newWorkflow: WorkflowInfo = (action as WorkflowUpdateAction)
        .payload;
      return state.map(workflow =>
        workflow.id === newWorkflow.id ? newWorkflow : workflow,
      );
    } else if (action.type === actionTypes.WORKFLOW_REMOVE.SUCCESS) {
      const newWorkflow: {id: WorkflowId} = (action as WorkflowRemoveAction)
        .payload;
      return state.filter(workflow => workflow.id !== newWorkflow.id);
    } else if (action.type === actionTypes.WORKFLOW_INSTANCE_ADD.SUCCESS) {
      const workflowInstanceAction = action as WorkflowInstanceAddAction;
      const newInstance = workflowInstanceAction.payload.instance;
      const archivedId = workflowInstanceAction.payload.archivedId;
      const workflowId = newInstance.workflow_id;

      return state.map(workflow => {
        if (workflow.id === workflowId) {
          const workflowInstances = workflow.workflow_instances || [];
          const unarchivedWorkflowInstances = workflowInstances.filter(
            instance => instance.id !== archivedId,
          );
          const updatedInstances = [
            newInstance,
            ...unarchivedWorkflowInstances,
          ];

          return {
            ...workflow,
            workflow_instances: updatedInstances,
          };
        }
        return workflow;
      });
    } else if (
      action.type === actionTypes.WORKFLOW_INSTANCE_UPDATE_NAME.SUCCESS
    ) {
      const updateAction = action as WorkflowInstanceUpdateNameAction;
      const {workflow_id, workflow_instance_ids, name} = updateAction.payload;

      return state.map(workflow => {
        if (workflow.id === workflow_id && workflow.workflow_instances) {
          const updatedInstances = workflow.workflow_instances.map(instance => {
            // if we return an array then we do if instance.id is in workflow_instance_ids
            if (workflow_instance_ids.includes(instance.id)) {
              return {
                ...instance,
                name,
              };
            }
            return instance;
          });

          return {
            ...workflow,
            workflow_instances: updatedInstances,
          };
        }
        return workflow;
      });
    } else if (
      action.type === actionTypes.WORKFLOW_INSTANCE_UPDATE_IS_ARCHIVED.SUCCESS
    ) {
      const updateAction = action as WorkflowInstanceUpdateIsArchivedAction;
      const {
        workflow_id,
        workflow_instance_id,
        is_archived,
      } = updateAction.payload;

      return state.map(workflow => {
        if (workflow.id === workflow_id && workflow.workflow_instances) {
          const updatedInstances = workflow.workflow_instances.map(instance => {
            if (instance.id === workflow_instance_id) {
              return {
                ...instance,
                is_archived,
              };
            }
            return instance;
          });

          return {
            ...workflow,
            workflow_instances: updatedInstances,
          };
        }
        return workflow;
      });
    } else if (
      action.type === actionTypes.WORKFLOW_INSTANCE_UPDATE_IS_PUBLIC.SUCCESS
    ) {
      const updateAction = action as WorkflowInstanceUpdateIsPublicAction;
      const {
        workflow_id,
        workflow_instance_id,
        is_public,
      } = updateAction.payload;

      return state.map(workflow => {
        if (workflow.id === workflow_id && workflow.workflow_instances) {
          const updatedInstances = workflow.workflow_instances.map(instance => {
            if (instance.id === workflow_instance_id) {
              return {
                ...instance,
                is_public,
              };
            }
            return instance;
          });

          return {
            ...workflow,
            workflow_instances: updatedInstances,
          };
        }
        return workflow;
      });
    } else if (
      action.type ===
      actionTypes.WORKFLOW_INSTANCE_UPDATE_DEFAULT_ASSET_NAME.SUCCESS
    ) {
      const updateAction = action as WorkflowInstanceUpdateDefaultAssetNameAction;
      const {workflow_instance_id, default_asset_name} = updateAction.payload;

      return state.map(workflow => {
        if (workflow.workflow_instances) {
          const updatedInstances = workflow.workflow_instances.map(instance => {
            if (instance.id === workflow_instance_id) {
              return {
                ...instance,
                config: {
                  ...instance.config,
                  default_asset_name,
                },
              };
            }
            return instance;
          });

          return {
            ...workflow,
            workflow_instances: updatedInstances,
          };
        }
        return workflow;
      });
    }
  }

  return state;
}
